import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faClock, 
  faCheckCircle, 
  faExclamationTriangle, 
  faTimesCircle,
  faSync,
  faPlay,
  faPlus,
  faEdit,
  faPause,
  faStop,
  faHistory,
  faCode,
  faTrash,
  faChevronLeft,
  faChevronDown,
  faChevronRight,
  faFile,
  faFolder,
  faUpload,
  faCopy,
  faFileCode
} from '@fortawesome/free-solid-svg-icons';

import api from '../../utils/api';
import { useTheme } from '../../context/ThemeContext';
import MainLayout from '../Layout/MainLayout';

const CronLogs = () => {
  const { isDarkMode } = useTheme();
  const [cronJobs, setCronJobs] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [jobExecutions, setJobExecutions] = useState([]);
  const [cronStatus, setCronStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [executionsLoading, setExecutionsLoading] = useState(false);
  const [expandedLogs, setExpandedLogs] = useState({});
  const [currentView, setCurrentView] = useState('jobs'); // 'jobs' or 'executions'
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [scriptInputMode, setScriptInputMode] = useState('command'); // 'command', 'path', 'paste', 'upload'
  const [cronInputMode, setCronInputMode] = useState('preset'); // 'preset', 'custom'
  const [newJob, setNewJob] = useState({
    name: '',
    description: '',
    cron_expression: '0 */2 * * *',
    command: '',
    script_path: '',
    script_content: '',
    enabled: true,
    timeout_seconds: 300
  });
  const [scriptValidation, setScriptValidation] = useState(null);
  const [showValidationWarning, setShowValidationWarning] = useState(false);
  const [showSchedulerConflictModal, setShowSchedulerConflictModal] = useState(false);

  // Convert cron expression to human readable format
  const cronToHuman = (cronExpr) => {
    const expressions = {
      // Minutes
      '* * * * *': 'Every minute',
      '*/2 * * * *': 'Every 2 minutes',
      '*/3 * * * *': 'Every 3 minutes',
      '*/5 * * * *': 'Every 5 minutes',
      '*/10 * * * *': 'Every 10 minutes',
      '*/15 * * * *': 'Every 15 minutes',
      '*/20 * * * *': 'Every 20 minutes',
      '*/30 * * * *': 'Every 30 minutes',
      '*/45 * * * *': 'Every 45 minutes',
      
      // Hours
      '0 * * * *': 'Every hour',
      '0 */2 * * *': 'Every 2 hours',
      '0 */3 * * *': 'Every 3 hours',
      '0 */4 * * *': 'Every 4 hours',
      '0 */6 * * *': 'Every 6 hours',
      '0 */8 * * *': 'Every 8 hours',
      '0 */12 * * *': 'Every 12 hours',
      
      // Daily
      '0 0 * * *': 'Daily at midnight',
      '0 6 * * *': 'Daily at 6:00 AM',
      '0 9 * * *': 'Daily at 9:00 AM',
      '0 12 * * *': 'Daily at noon',
      '0 15 * * *': 'Daily at 3:00 PM',
      '0 18 * * *': 'Daily at 6:00 PM',
      '0 21 * * *': 'Daily at 9:00 PM',
      
      // Weekly
      '0 0 * * 0': 'Weekly on Sunday',
      '0 0 * * 1': 'Weekly on Monday',
      '0 0 * * 2': 'Weekly on Tuesday',
      '0 0 * * 3': 'Weekly on Wednesday',
      '0 0 * * 4': 'Weekly on Thursday',
      '0 0 * * 5': 'Weekly on Friday',
      '0 0 * * 6': 'Weekly on Saturday',
      
      // Monthly & Yearly
      '0 0 1 * *': 'Monthly on 1st',
      '0 0 15 * *': 'Monthly on 15th',
      '0 0 1 1 *': 'Yearly on January 1st'
    };
    
    return expressions[cronExpr] || cronExpr;
  };

  // Get preset cron expressions
  const cronPresets = [
    // Minutes
    { value: '* * * * *', label: 'Every minute' },
    { value: '*/2 * * * *', label: 'Every 2 minutes' },
    { value: '*/3 * * * *', label: 'Every 3 minutes' },
    { value: '*/5 * * * *', label: 'Every 5 minutes' },
    { value: '*/10 * * * *', label: 'Every 10 minutes' },
    { value: '*/15 * * * *', label: 'Every 15 minutes' },
    { value: '*/20 * * * *', label: 'Every 20 minutes' },
    { value: '*/30 * * * *', label: 'Every 30 minutes' },
    { value: '*/45 * * * *', label: 'Every 45 minutes' },
    
    // Hours
    { value: '0 * * * *', label: 'Every hour' },
    { value: '0 */2 * * *', label: 'Every 2 hours' },
    { value: '0 */3 * * *', label: 'Every 3 hours' },
    { value: '0 */4 * * *', label: 'Every 4 hours' },
    { value: '0 */6 * * *', label: 'Every 6 hours' },
    { value: '0 */8 * * *', label: 'Every 8 hours' },
    { value: '0 */12 * * *', label: 'Every 12 hours' },
    
    // Daily
    { value: '0 0 * * *', label: 'Daily at midnight' },
    { value: '0 6 * * *', label: 'Daily at 6:00 AM' },
    { value: '0 9 * * *', label: 'Daily at 9:00 AM' },
    { value: '0 12 * * *', label: 'Daily at noon' },
    { value: '0 15 * * *', label: 'Daily at 3:00 PM' },
    { value: '0 18 * * *', label: 'Daily at 6:00 PM' },
    { value: '0 21 * * *', label: 'Daily at 9:00 PM' },
    
    // Weekly
    { value: '0 0 * * 0', label: 'Weekly on Sunday' },
    { value: '0 0 * * 1', label: 'Weekly on Monday' },
    { value: '0 0 * * 2', label: 'Weekly on Tuesday' },
    { value: '0 0 * * 3', label: 'Weekly on Wednesday' },
    { value: '0 0 * * 4', label: 'Weekly on Thursday' },
    { value: '0 0 * * 5', label: 'Weekly on Friday' },
    { value: '0 0 * * 6', label: 'Weekly on Saturday' },
    
    // Monthly & Yearly
    { value: '0 0 1 * *', label: 'Monthly on 1st' },
    { value: '0 0 15 * *', label: 'Monthly on 15th' },
    { value: '0 0 1 1 *', label: 'Yearly on January 1st' }
  ];

  // Status colors
  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': case 'active': case 'healthy': return 'text-green-500';
      case 'failed': case 'error': return 'text-red-500';
      case 'running': return 'text-blue-500';
      case 'timeout': case 'delayed': return 'text-orange-500';
      case 'inactive': return 'text-gray-500';
      default: return 'text-gray-400';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': case 'active': case 'healthy': return faCheckCircle;
      case 'failed': case 'error': return faTimesCircle;
      case 'running': return faSync;
      case 'timeout': case 'delayed': return faExclamationTriangle;
      case 'inactive': return faPause;
      default: return faClock;
    }
  };

  // Fetch cron jobs
  const fetchCronJobs = async () => {
    try {
      setLoading(true);
      console.log('🔍 Fetching cron jobs from /api/cron/jobs...');
      const response = await api.get('/api/cron/jobs');
      console.log('📋 Jobs Response:', response);
      
      const data = response?.data || response;
      if (data && data.success) {
        console.log('✅ Found jobs:', data.jobs?.length || 0);
        setCronJobs(data.jobs || []);
      } else {
        console.log('❌ No jobs found');
        setCronJobs([]);
      }
    } catch (error) {
      console.error('❌ Error fetching cron jobs:', error);
      setCronJobs([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch job executions
  const fetchJobExecutions = async (jobId, page = 1) => {
    try {
      setExecutionsLoading(true);
      const response = await api.get(`/api/cron/jobs/${jobId}/executions?page=${page}&per_page=20`);
      console.log('🔍 [DEBUG] Job executions response:', response);
      
      const data = response?.data || response;
      console.log('🔍 [DEBUG] Processed executions data:', data);
      console.log('🔍 [DEBUG] Executions array:', data?.executions);
      
      if (data && data.success) {
        const executions = data.executions || [];
        console.log('🔍 [DEBUG] Setting executions:', executions);
        // Log each execution to see the structure
        executions.forEach((exec, idx) => {
          console.log(`🔍 [DEBUG] Execution ${idx}:`, exec);
          console.log(`🔍 [DEBUG] Execution ${idx} execution_time_seconds:`, exec.execution_time_seconds);
          console.log(`🔍 [DEBUG] Execution ${idx} execution_time:`, exec.execution_time);
        });
        setJobExecutions(executions);
      } else {
        console.log('❌ [DEBUG] No successful data, setting empty executions');
        setJobExecutions([]);
      }
    } catch (error) {
      console.error('❌ Error fetching job executions:', error);
      setJobExecutions([]);
    } finally {
      setExecutionsLoading(false);
    }
  };

  // Fetch cron system status
  const fetchCronStatus = async () => {
    try {
      const response = await api.get('/api/cron/status');
      const data = response?.data || response;
      if (data && data.success) {
        setCronStatus(data);
      }
    } catch (error) {
      console.error('Error fetching cron status:', error);
    }
  };

  // Validate script for scheduler conflicts
  const validateScript = async (scriptContent, command) => {
    try {
      console.log('🔍 [FRONTEND] Calling validation API...');
      console.log('🔍 [FRONTEND] Script content length:', (scriptContent || '').length);
      console.log('🔍 [FRONTEND] Command:', command || 'none');
      
      const response = await api.post('/api/cron/validate-script', {
        script_content: scriptContent || '',
        command: command || ''
      });
      
      console.log('🔍 [FRONTEND] Validation API response:', response);
      
      const data = response?.data || response;
      if (data && data.success) {
        console.log('🔍 [FRONTEND] Validation result:', data.validation);
        setScriptValidation(data.validation);
        setShowValidationWarning(data.validation.has_conflicts);
        
        if (data.validation.has_conflicts) {
          console.log('🚨 [FRONTEND] CONFLICTS DETECTED! Showing warning...');
        } else {
          console.log('✅ [FRONTEND] No conflicts detected');
        }
      }
    } catch (error) {
      console.error('❌ [FRONTEND] Script validation error:', error);
      setScriptValidation(null);
    }
  };

  // Handle file upload
  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    console.log('📁 [FRONTEND] File selected:', file?.name);
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = async (e) => {
      const content = e.target.result;
      console.log('📁 [FRONTEND] File content loaded, length:', content?.length);
      console.log('📁 [FRONTEND] File content preview:', content?.substring(0, 100));
      
      const updatedJob = {
        ...newJob,
        script_content: content,
        name: newJob.name || file.name.replace(/\.[^/.]+$/, '') // Set name if empty
      };
      setNewJob(updatedJob);
      
      // Validate script for conflicts (only validate content for uploads)
      await validateScript(content, '');
      
      console.log('📁 [FRONTEND] Script content set in state');
    };
    reader.readAsText(file);
  };

  // Handle script content changes with validation
  const handleScriptContentChange = async (content, command) => {
    if (content || command) {
      await validateScript(content, command);
    } else {
      setScriptValidation(null);
      setShowValidationWarning(false);
    }
  };

  // Process script input based on mode  
  const getJobData = () => {
    const baseData = {
      name: newJob.name,
      description: newJob.description,
      cron_expression: newJob.cron_expression,
      enabled: newJob.enabled,
      timeout_seconds: newJob.timeout_seconds
    };

    switch (scriptInputMode) {
      case 'command':
        return {
          ...baseData,
          command: newJob.command,
          script_path: '',
          script_content: ''
        };
      case 'path':
        return {
          ...baseData,
          command: newJob.script_path,
          script_path: newJob.script_path,
          script_content: ''
        };
      case 'paste':
      case 'upload':
        return {
          ...baseData,
          command: 'SCRIPT_CONTENT',
          script_path: '',
          script_content: newJob.script_content
        };
      default:
        return {
          ...baseData,
          command: newJob.command,
          script_path: '',
          script_content: ''
        };
    }
  };

  // Validate job data
  const isJobValid = () => {
    if (!newJob.name || !newJob.cron_expression) {
      return false;
    }
    
    switch (scriptInputMode) {
      case 'command':
        return !!newJob.command;
      case 'path':
        return !!newJob.script_path;
      case 'paste':
      case 'upload':
        return !!newJob.script_content;
      default:
        return false;
    }
  };

  // Open edit modal
  const openEditModal = (job) => {
    setEditingJob(job);
    setNewJob({
      name: job.name,
      description: job.description || '',
      cron_expression: job.cron_expression,
      command: job.command,
      script_path: job.script_path || '',
      script_content: job.script_content || '',
      enabled: job.enabled,
      timeout_seconds: job.timeout_seconds || 300
    });
    
    // Determine input mode based on existing data
    if (job.script_path) {
      setScriptInputMode('path');
    } else if (job.script_content) {
      setScriptInputMode('paste');
    } else {
      setScriptInputMode('command');
    }
    
    // Determine cron input mode
    const isPreset = cronPresets.some(preset => preset.value === job.cron_expression);
    setCronInputMode(isPreset ? 'preset' : 'custom');
    
    setShowEditModal(true);
  };

  // Update existing cron job
  const updateCronJob = async () => {
    try {
      const jobData = getJobData();
      const response = await api.put(`/api/cron/jobs/${editingJob.id}`, jobData);
      const data = response?.data || response;
      
      if (data && data.success) {
        console.log('✅ Job updated successfully');
        setShowEditModal(false);
        setEditingJob(null);
        setNewJob({
          name: '',
          description: '',
          cron_expression: '0 */2 * * *',
          command: '',
          script_path: '',
          script_content: '',
          enabled: true,
          timeout_seconds: 300
        });
        setScriptInputMode('command');
        setCronInputMode('preset');
        fetchCronJobs();
        fetchCronStatus();
      }
    } catch (error) {
      console.error('❌ Error updating cron job:', error);
    }
  };

  // Create new cron job
  const createCronJob = async () => {
    try {
      // Check for scheduler conflicts only
      if (scriptValidation && scriptValidation.has_scheduler_conflicts) {
        setShowSchedulerConflictModal(true);
        console.log('🚫 [FRONTEND] Job creation blocked due to scheduler conflicts');
        return;
      }
      
      const jobData = getJobData();
      console.log('🔍 [FRONTEND] Creating job with data:', jobData);
      console.log('🔍 [FRONTEND] Script input mode:', scriptInputMode);
      console.log('🔍 [FRONTEND] Job validation:', isJobValid());
      console.log('🔍 [FRONTEND] Script content length:', newJob.script_content?.length || 0);
      
      const response = await api.post('/api/cron/jobs', jobData);
      const data = response?.data || response;
      
      if (data && data.success) {
        console.log('✅ Job created successfully');
        setShowCreateModal(false);
        setNewJob({
          name: '',
          description: '',
          cron_expression: '0 */2 * * *',
          command: '',
          script_path: '',
          script_content: '',
          enabled: true,
          timeout_seconds: 300
        });
        setScriptInputMode('command');
        setCronInputMode('preset');
        fetchCronJobs();
        fetchCronStatus();
      }
    } catch (error) {
      console.error('❌ Error creating cron job:', error);
    }
  };

  // Execute job manually
  const executeJob = async (jobId) => {
    try {
      console.log(`🚀 [FRONTEND] Starting manual execution of job ${jobId}...`);
      console.log(`🚀 [FRONTEND] API endpoint: /api/cron/jobs/${jobId}/execute`);
      
      const response = await api.post(`/api/cron/jobs/${jobId}/execute`);
      console.log(`🚀 [FRONTEND] Manual execution response:`, response);
      
      const data = response?.data || response;
      console.log(`🚀 [FRONTEND] Processed response data:`, data);
      
      if (data && data.success) {
        console.log(`✅ [FRONTEND] Job ${jobId} executed successfully`);
        console.log(`✅ [FRONTEND] Execution details:`, {
          executionId: data.execution_id,
          status: data.status,
          message: data.message
        });
        
        console.log(`🔄 [FRONTEND] Refreshing data after execution...`);
        fetchCronJobs();
        fetchCronStatus();
        if (selectedJob && selectedJob.id === jobId) {
          console.log(`🔄 [FRONTEND] Refreshing executions for selected job ${jobId}...`);
          fetchJobExecutions(jobId);
        }
      } else {
        console.error(`❌ [FRONTEND] Job execution failed:`, data);
      }
    } catch (error) {
      console.error('❌ [FRONTEND] Error executing job:', error);
      console.error('❌ [FRONTEND] Error details:', {
        message: error.message,
        response: error.response,
        status: error.response?.status,
        data: error.response?.data
      });
    }
  };

  // Toggle job enabled status
  const toggleJobStatus = async (jobId, currentStatus) => {
    try {
      const response = await api.put(`/api/cron/jobs/${jobId}`, {
        enabled: !currentStatus
      });
      
      const data = response?.data || response;
      if (data && data.success) {
        fetchCronJobs();
      }
    } catch (error) {
      console.error('❌ Error updating job status:', error);
    }
  };

  // Delete job
  const deleteJob = async (jobId) => {
    if (!window.confirm('Are you sure you want to delete this job?')) {
      return;
    }
    
    try {
      const response = await api.delete(`/api/cron/jobs/${jobId}`);
      const data = response?.data || response;
      
      if (data && data.success) {
        fetchCronJobs();
        if (selectedJob && selectedJob.id === jobId) {
          setSelectedJob(null);
          setCurrentView('jobs');
        }
      }
    } catch (error) {
      console.error('❌ Error deleting job:', error);
    }
  };

  // View job executions
  const viewJobExecutions = (job) => {
    setSelectedJob(job);
    setCurrentView('executions');
    fetchJobExecutions(job.id);
  };

  // Toggle log expansion
  const toggleLogExpansion = (logIndex) => {
    setExpandedLogs(prev => ({
      ...prev,
      [logIndex]: !prev[logIndex]
    }));
  };

  // Auto-refresh data
  useEffect(() => {
    fetchCronJobs();
    fetchCronStatus();

    // Set up auto-refresh every 30 seconds
    const interval = setInterval(() => {
      fetchCronJobs();
      fetchCronStatus();
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  const pageVariants = {
    initial: { opacity: 0, y: 20 },
    in: { opacity: 1, y: 0 },
    out: { opacity: 0, y: -20 }
  };

  return (
    <MainLayout title="Cron Jobs Manager">
      <div className="p-6 relative">
        <motion.div
          initial="initial"
          animate="in"
          exit="out"
          variants={pageVariants}
          transition={{ duration: 0.3 }}
        >
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            {currentView === 'executions' && (
              <button
                onClick={() => setCurrentView('jobs')}
                className="mr-4 p-2 rounded-lg bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600"
              >
                <FontAwesomeIcon icon={faChevronLeft} />
              </button>
            )}
            <div>
              <h1 className="text-3xl font-bold flex items-center">
                <FontAwesomeIcon icon={faClock} className="mr-3 text-blue-500" />
                {currentView === 'jobs' ? 'Cron Jobs Manager' : `Executions - ${selectedJob?.name}`}
              </h1>
              <p className={`mt-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                {currentView === 'jobs' 
                  ? 'Create and manage automated scheduled jobs' 
                  : 'View execution history and logs for this job'
                }
              </p>
            </div>
          </div>
          
          <div className="flex gap-3">
            <button
              onClick={() => {
                if (currentView === 'jobs') {
                  fetchCronJobs();
                  fetchCronStatus();
                } else {
                  fetchJobExecutions(selectedJob.id);
                }
              }}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                isDarkMode 
                  ? 'bg-blue-600 hover:bg-blue-700 text-white' 
                  : 'bg-blue-500 hover:bg-blue-600 text-white'
              }`}
            >
              <FontAwesomeIcon icon={faSync} className="mr-2" />
              Refresh
            </button>
            
            {currentView === 'jobs' && (
              <button
                onClick={() => setShowCreateModal(true)}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  isDarkMode 
                    ? 'bg-green-600 hover:bg-green-700 text-white' 
                    : 'bg-green-500 hover:bg-green-600 text-white'
                }`}
              >
                <FontAwesomeIcon icon={faPlus} className="mr-2" />
                Create Job
              </button>
            )}
          </div>
        </div>

        {/* Status Overview */}
        {cronStatus && currentView === 'jobs' && (
          <div className="rounded-xl p-6 mb-6 bg-white dark:bg-gray-800 shadow-lg">
            <h2 className="text-xl font-semibold mb-4">System Status</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {/* Total Jobs */}
              <div className={`p-4 rounded-lg ${
                isDarkMode ? 'bg-gray-700' : 'bg-gray-50'
              }`}>
                <div className="flex items-center">
                  <FontAwesomeIcon icon={faClock} className="mr-3 text-xl text-blue-500" />
                  <div>
                    <p className="text-sm text-gray-500">Total Jobs</p>
                    <p className="font-semibold">{cronStatus.statistics?.total_jobs || 0}</p>
                  </div>
                </div>
              </div>

              {/* Active Jobs */}
              <div className={`p-4 rounded-lg ${
                isDarkMode ? 'bg-gray-700' : 'bg-gray-50'
              }`}>
                <div className="flex items-center">
                  <FontAwesomeIcon icon={faCheckCircle} className="mr-3 text-xl text-green-500" />
                  <div>
                    <p className="text-sm text-gray-500">Active Jobs</p>
                    <p className="font-semibold">{cronStatus.statistics?.active_jobs || 0}</p>
                  </div>
                </div>
              </div>

              {/* Total Executions */}
              <div className={`p-4 rounded-lg ${
                isDarkMode ? 'bg-gray-700' : 'bg-gray-50'
              }`}>
                <div className="flex items-center">
                  <FontAwesomeIcon icon={faPlay} className="mr-3 text-xl text-purple-500" />
                  <div>
                    <p className="text-sm text-gray-500">Total Executions</p>
                    <p className="font-semibold">{cronStatus.statistics?.total_executions || 0}</p>
                  </div>
                </div>
              </div>

              {/* System Health */}
              <div className={`p-4 rounded-lg ${
                isDarkMode ? 'bg-gray-700' : 'bg-gray-50'
              }`}>
                <div className="flex items-center">
                  <FontAwesomeIcon 
                    icon={getStatusIcon(cronStatus.system_status)} 
                    className={`mr-3 text-xl ${getStatusColor(cronStatus.system_status)}`}
                  />
                  <div>
                    <p className="text-sm text-gray-500">System Status</p>
                    <p className="font-semibold capitalize">{cronStatus.system_status || 'Unknown'}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Jobs List View */}
        {currentView === 'jobs' && (
          <div className="rounded-xl bg-white dark:bg-gray-800 shadow-lg">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-xl font-semibold">Cron Jobs</h2>
            </div>
            
            <div className="p-6">
              {loading ? (
                <div className="flex justify-center py-8">
                  <FontAwesomeIcon icon={faSync} className="animate-spin text-2xl text-blue-500" />
                </div>
              ) : cronJobs.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <FontAwesomeIcon icon={faClock} className="text-4xl mb-4" />
                  <p className="text-lg mb-2">No cron jobs found</p>
                  <p>Create your first automated job to get started</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {cronJobs.map((job, index) => (
                    <motion.div
                      key={job.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className={`border rounded-lg p-4 ${
                        isDarkMode ? 'border-gray-700 hover:border-gray-600' : 'border-gray-200 hover:border-gray-300'
                      } transition-colors`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <FontAwesomeIcon 
                              icon={getStatusIcon(job.status)} 
                              className={`${getStatusColor(job.status)}`}
                            />
                            <h3 className="font-semibold text-lg">{job.name}</h3>
                            {!job.enabled && (
                              <span className={`px-2 py-1 rounded-full text-xs ${
                                isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-600'
                              }`}>
                                DISABLED
                              </span>
                            )}
                          </div>
                          
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-500 mb-2">
                            <div>
                              <FontAwesomeIcon icon={faClock} className="mr-2" />
                              Schedule: {cronToHuman(job.cron_expression)}
                            </div>
                            <div>
                              <FontAwesomeIcon icon={faPlay} className="mr-2" />
                              Executions: {job.execution_count || 0}
                            </div>
                            <div>
                              <FontAwesomeIcon icon={faTimesCircle} className="mr-2" />
                              Failures: {job.failure_count || 0}
                            </div>
                          </div>
                          
                          {job.description && (
                            <p className="text-gray-600 dark:text-gray-400 mb-2">{job.description}</p>
                          )}
                          
                          <div className="flex items-center gap-2 text-xs text-gray-500">
                            <FontAwesomeIcon icon={faCode} />
                            <code className={`px-2 py-1 rounded ${
                              isDarkMode ? 'bg-gray-700' : 'bg-gray-100'
                            }`}>
                              {job.command}
                            </code>
                          </div>
                        </div>
                        
                        <div className="flex flex-col gap-2 ml-4">
                          <button
                            onClick={() => executeJob(job.id)}
                            className={`px-3 py-1 rounded text-sm ${
                              isDarkMode 
                                ? 'bg-blue-600 hover:bg-blue-700 text-white' 
                                : 'bg-blue-500 hover:bg-blue-600 text-white'
                            }`}
                          >
                            <FontAwesomeIcon icon={faPlay} className="mr-1" />
                            Run
                          </button>

                          <button
                            onClick={() => openEditModal(job)}
                            className={`px-3 py-1 rounded text-sm ${
                              isDarkMode 
                                ? 'bg-green-600 hover:bg-green-700 text-white' 
                                : 'bg-green-500 hover:bg-green-600 text-white'
                            }`}
                          >
                            <FontAwesomeIcon icon={faEdit} className="mr-1" />
                            Edit
                          </button>
                          
                          <button
                            onClick={() => viewJobExecutions(job)}
                            className={`px-3 py-1 rounded text-sm ${
                              isDarkMode 
                                ? 'bg-purple-600 hover:bg-purple-700 text-white' 
                                : 'bg-purple-500 hover:bg-purple-600 text-white'
                            }`}
                          >
                            <FontAwesomeIcon icon={faHistory} className="mr-1" />
                            History
                          </button>
                          
                          <button
                            onClick={() => toggleJobStatus(job.id, job.enabled)}
                            className={`px-3 py-1 rounded text-sm ${
                              job.enabled 
                                ? 'bg-orange-500 hover:bg-orange-600 text-white' 
                                : 'bg-green-500 hover:bg-green-600 text-white'
                            }`}
                          >
                            <FontAwesomeIcon icon={job.enabled ? faPause : faPlay} className="mr-1" />
                            {job.enabled ? 'Pause' : 'Enable'}
                          </button>
                          
                          <button
                            onClick={() => deleteJob(job.id)}
                            className="px-3 py-1 rounded text-sm bg-red-500 hover:bg-red-600 text-white"
                          >
                            <FontAwesomeIcon icon={faTrash} className="mr-1" />
                            Delete
                          </button>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Executions View */}
        {currentView === 'executions' && selectedJob && (
          <div className="rounded-xl bg-white dark:bg-gray-800 shadow-lg">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold">Execution History</h2>
                <button
                  onClick={() => executeJob(selectedJob.id)}
                  className={`px-4 py-2 rounded-lg font-medium ${
                    isDarkMode 
                      ? 'bg-blue-600 hover:bg-blue-700 text-white' 
                      : 'bg-blue-500 hover:bg-blue-600 text-white'
                  }`}
                >
                  <FontAwesomeIcon icon={faPlay} className="mr-2" />
                  Run Now
                </button>
              </div>
            </div>
            
            <div className="p-6">
              {executionsLoading ? (
                <div className="flex justify-center py-8">
                  <FontAwesomeIcon icon={faSync} className="animate-spin text-2xl text-blue-500" />
                </div>
              ) : jobExecutions.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <FontAwesomeIcon icon={faHistory} className="text-4xl mb-4" />
                  <p>No executions found for this job</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {jobExecutions.map((execution, index) => (
                    <motion.div
                      key={execution.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className={`border rounded-lg p-4 ${
                        isDarkMode ? 'border-gray-700 hover:border-gray-600' : 'border-gray-200 hover:border-gray-300'
                      } transition-colors cursor-pointer`}
                      onClick={() => toggleLogExpansion(index)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <FontAwesomeIcon 
                            icon={getStatusIcon(execution.status)} 
                            className={`mr-3 ${getStatusColor(execution.status)}`}
                          />
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <p className="font-medium">
                                {new Date(execution.started_at).toLocaleString()}
                              </p>
                              <span className={`px-2 py-1 rounded-full text-xs ${
                                execution.status === 'completed' 
                                  ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-200'
                                  : execution.status === 'failed'
                                  ? 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-200'
                                  : 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-200'
                              }`}>
                                {execution.status.toUpperCase()}
                              </span>
                              {execution.triggered_by === 'manual' && (
                                <span className={`px-2 py-1 rounded-full text-xs ${
                                  isDarkMode ? 'bg-purple-900 text-purple-200' : 'bg-purple-100 text-purple-700'
                                }`}>
                                  MANUAL
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-gray-500">
                              Duration: {(() => {
                                if (execution.status === 'running') {
                                  return 'Running...';
                                }
                                
                                if (execution.execution_time_seconds) {
                                  return `${parseFloat(execution.execution_time_seconds).toFixed(2)}s`;
                                } else if (execution.execution_time) {
                                  return `${parseFloat(execution.execution_time).toFixed(2)}s`;
                                } else {
                                  return 'Unknown';
                                }
                              })()}
                              {execution.exit_code !== null && (
                                <span className="ml-3">Exit Code: {execution.exit_code}</span>
                              )}
                            </p>
                          </div>
                        </div>
                        
                        <FontAwesomeIcon 
                          icon={expandedLogs[index] ? faChevronDown : faChevronRight}
                          className="text-gray-400"
                        />
                      </div>
                      
                      <AnimatePresence>
                        {expandedLogs[index] && (execution.output || execution.print_logs || execution.logger_logs || execution.execution_logs || execution.error_message) && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700"
                          >
                            {execution.output && (
                              <div className="mb-4">
                                <p className="text-sm font-medium mb-2 text-green-600 dark:text-green-400">📄 Output (Summary):</p>
                                <pre className={`p-3 rounded text-xs overflow-auto max-h-60 ${
                                  isDarkMode ? 'bg-green-900 text-green-200' : 'bg-green-50 text-green-800'
                                }`}>
                                  {execution.output}
                                </pre>
                              </div>
                            )}
                            
                            {execution.print_logs && (
                              <div className="mb-4">
                                <p className="text-sm font-medium mb-2 text-blue-600 dark:text-blue-400">🖨️ Print Logs (stdout):</p>
                                <pre className={`p-3 rounded text-xs overflow-auto max-h-60 ${
                                  isDarkMode ? 'bg-blue-900 text-blue-200' : 'bg-blue-50 text-blue-800'
                                }`}>
                                  {execution.print_logs}
                                </pre>
                              </div>
                            )}
                            
                            {execution.logger_logs && (
                              <div className="mb-4">
                                <p className="text-sm font-medium mb-2 text-purple-600 dark:text-purple-400">📝 Logger Logs (from log files):</p>
                                <pre className={`p-3 rounded text-xs overflow-auto max-h-60 ${
                                  isDarkMode ? 'bg-purple-900 text-purple-200' : 'bg-purple-50 text-purple-800'
                                }`}>
                                  {execution.logger_logs}
                                </pre>
                              </div>
                            )}
                            
                            {execution.execution_logs && (
                              <div className="mb-4">
                                <p className="text-sm font-medium mb-2 text-gray-600 dark:text-gray-400">🔍 Full Execution Logs:</p>
                                <pre className={`p-3 rounded text-xs overflow-auto max-h-60 ${
                                  isDarkMode ? 'bg-gray-900 text-gray-300' : 'bg-gray-50 text-gray-800'
                                }`}>
                                  {execution.execution_logs}
                                </pre>
                              </div>
                            )}
                            
                            {execution.error_message && (
                              <div>
                                <p className="text-sm font-medium mb-2 text-red-500">❌ Error:</p>
                                <pre className={`p-3 rounded text-xs overflow-auto max-h-60 ${
                                  isDarkMode ? 'bg-red-900 text-red-200' : 'bg-red-50 text-red-800'
                                }`}>
                                  {execution.error_message}
                                </pre>
                              </div>
                            )}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </motion.div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </motion.div>

      {/* Create Job Modal */}
      <AnimatePresence>
        {showCreateModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
            onClick={(e) => e.target === e.currentTarget && setShowCreateModal(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white dark:bg-gray-800 rounded-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto"
            >
              <h3 className="text-xl font-semibold mb-4">Create New Cron Job</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Job Name *</label>
                  <input
                    type="text"
                    value={newJob.name}
                    onChange={(e) => setNewJob({...newJob, name: e.target.value})}
                    className={`w-full px-3 py-2 border rounded-lg ${
                      isDarkMode 
                        ? 'bg-gray-700 border-gray-600 text-white' 
                        : 'bg-white border-gray-300 text-gray-900'
                    }`}
                    placeholder="e.g., Daily Backup"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2">Description</label>
                  <textarea
                    value={newJob.description}
                    onChange={(e) => setNewJob({...newJob, description: e.target.value})}
                    className={`w-full px-3 py-2 border rounded-lg ${
                      isDarkMode 
                        ? 'bg-gray-700 border-gray-600 text-white' 
                        : 'bg-white border-gray-300 text-gray-900'
                    }`}
                    rows="2"
                    placeholder="Brief description of what this job does"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Schedule *
                  </label>
                  
                  {/* Schedule Input Mode Tabs */}
                  <div className="flex mb-3 border-b border-gray-300 dark:border-gray-600">
                    <button
                      type="button"
                      onClick={() => setCronInputMode('preset')}
                      className={`px-3 py-2 text-sm font-medium border-b-2 transition-colors ${
                        cronInputMode === 'preset'
                          ? 'border-blue-500 text-blue-500'
                          : 'border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
                      }`}
                    >
                      <FontAwesomeIcon icon={faClock} className="mr-1" />
                      Quick Schedule
                    </button>
                    <button
                      type="button"
                      onClick={() => setCronInputMode('custom')}
                      className={`px-3 py-2 text-sm font-medium border-b-2 transition-colors ${
                        cronInputMode === 'custom'
                          ? 'border-blue-500 text-blue-500'
                          : 'border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
                      }`}
                    >
                      <FontAwesomeIcon icon={faCode} className="mr-1" />
                      Custom Expression
                    </button>
                  </div>

                  {/* Preset Schedule Dropdown */}
                  {cronInputMode === 'preset' && (
                    <div>
                      <select
                        value={newJob.cron_expression}
                        onChange={(e) => setNewJob({...newJob, cron_expression: e.target.value})}
                        className={`w-full px-3 py-2 border rounded-lg ${
                          isDarkMode 
                            ? 'bg-gray-700 border-gray-600 text-white' 
                            : 'bg-white border-gray-300 text-gray-900'
                        }`}
                      >
                        {cronPresets.map((preset) => (
                          <option key={preset.value} value={preset.value}>
                            {preset.label}
                          </option>
                        ))}
                      </select>
                      <p className="text-xs text-gray-500 mt-1">
                        Choose from common scheduling patterns
                      </p>
                    </div>
                  )}

                  {/* Custom Cron Expression */}
                  {cronInputMode === 'custom' && (
                    <div>
                      <input
                        type="text"
                        value={newJob.cron_expression}
                        onChange={(e) => setNewJob({...newJob, cron_expression: e.target.value})}
                        className={`w-full px-3 py-2 border rounded-lg ${
                          isDarkMode 
                            ? 'bg-gray-700 border-gray-600 text-white' 
                            : 'bg-white border-gray-300 text-gray-900'
                        }`}
                        placeholder="0 */2 * * *"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Format: minute hour day month weekday<br/>
                        Examples: "0 */2 * * *" (every 2 hours), "*/15 * * * *" (every 15 minutes)
                      </p>
                    </div>
                  )}

                  {/* Show human readable preview */}
                  <div className={`mt-2 p-2 rounded-lg text-sm ${
                    isDarkMode ? 'bg-gray-700' : 'bg-blue-50'
                  }`}>
                    <FontAwesomeIcon icon={faClock} className="mr-2 text-blue-500" />
                    <span className="text-blue-600 dark:text-blue-400">
                      Will run: {cronToHuman(newJob.cron_expression)}
                    </span>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2">Script/Command *</label>
                  
                  {/* Script Input Mode Tabs */}
                  <div className="flex mb-3 border-b border-gray-300 dark:border-gray-600">
                    {[
                      { key: 'command', label: 'Command', icon: faCode },
                      { key: 'path', label: 'File Path', icon: faFolder },
                      { key: 'paste', label: 'Paste Script', icon: faCopy },
                      { key: 'upload', label: 'Upload File', icon: faUpload }
                    ].map((tab) => (
                      <button
                        key={tab.key}
                        type="button"
                        onClick={() => setScriptInputMode(tab.key)}
                        className={`px-3 py-2 text-sm font-medium border-b-2 transition-colors ${
                          scriptInputMode === tab.key
                            ? 'border-blue-500 text-blue-500'
                            : 'border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
                        }`}
                      >
                        <FontAwesomeIcon icon={tab.icon} className="mr-1" />
                        {tab.label}
                      </button>
                    ))}
                  </div>

                  {/* Command Input */}
                  {scriptInputMode === 'command' && (
                    <textarea
                      value={newJob.command}
                      onChange={async (e) => {
                        const command = e.target.value;
                        console.log('🔍 [FRONTEND] Command changed, triggering validation...');
                        setNewJob({...newJob, command});
                        await handleScriptContentChange(newJob.script_content, command);
                      }}
                      className={`w-full px-3 py-2 border rounded-lg ${
                        isDarkMode 
                          ? 'bg-gray-700 border-gray-600 text-white' 
                          : 'bg-white border-gray-300 text-gray-900'
                      }`}
                      rows="3"
                      placeholder="python /path/to/script.py"
                    />
                  )}

                  {/* File Path Input */}
                  {scriptInputMode === 'path' && (
                    <div>
                      <input
                        type="text"
                        value={newJob.script_path}
                        onChange={async (e) => {
                          const path = e.target.value;
                          console.log('🔍 [FRONTEND] Script path changed:', path);
                          setNewJob({...newJob, script_path: path});
                          
                          // For path mode, don't validate file content (backend will handle file reading)
                          // Only validate if user wants to restrict path-based validation
                          if (path.trim()) {
                            // Skip validation for path mode - let backend handle it
                            setScriptValidation(null);
                            setShowValidationWarning(false);
                          } else {
                            setScriptValidation(null);
                            setShowValidationWarning(false);
                          }
                        }}
                        className={`w-full px-3 py-2 border rounded-lg ${
                          isDarkMode 
                            ? 'bg-gray-700 border-gray-600 text-white' 
                            : 'bg-white border-gray-300 text-gray-900'
                        }`}
                        placeholder="/path/to/your/script.py"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Full path to your script file (e.g., Python, batch, shell script)
                      </p>
                    </div>
                  )}

                  {/* Paste Script Input */}
                  {scriptInputMode === 'paste' && (
                    <div>
                      <textarea
                        value={newJob.script_content}
                        onChange={async (e) => {
                          const content = e.target.value;
                          console.log('🔍 [FRONTEND] Paste script content changed, triggering validation...');
                          setNewJob({...newJob, script_content: content});
                          await handleScriptContentChange(content, newJob.command);
                        }}
                        className={`w-full px-3 py-2 border rounded-lg font-mono text-sm ${
                          isDarkMode 
                            ? 'bg-gray-700 border-gray-600 text-white' 
                            : 'bg-white border-gray-300 text-gray-900'
                        }`}
                        rows="8"
                        placeholder={`# Python script example
import datetime

print(f"Script executed at: {datetime.datetime.now()}")
print("Hello from cron job!")`}
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Paste your complete script here. It will be executed as Python code.
                      </p>
                    </div>
                  )}

                  {/* File Upload Input */}
                  {scriptInputMode === 'upload' && (
                    <div>
                      <div className={`border-2 border-dashed rounded-lg p-6 text-center ${
                        isDarkMode 
                          ? 'border-gray-600 hover:border-gray-500' 
                          : 'border-gray-300 hover:border-gray-400'
                      } transition-colors`}>
                        <FontAwesomeIcon icon={faFileCode} className="text-3xl text-gray-400 mb-3" />
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                          Click to upload a script file or drag and drop
                        </p>
                        <input
                          type="file"
                          onChange={handleFileUpload}
                          accept=".py,.js,.bat,.sh,.ps1,.txt"
                          className="hidden"
                          id="script-upload"
                        />
                        <label
                          htmlFor="script-upload"
                          className={`inline-flex items-center px-4 py-2 rounded-lg cursor-pointer transition-colors ${
                            isDarkMode
                              ? 'bg-blue-600 hover:bg-blue-700 text-white'
                              : 'bg-blue-500 hover:bg-blue-600 text-white'
                          }`}
                        >
                          <FontAwesomeIcon icon={faUpload} className="mr-2" />
                          Choose File
                        </label>
                        {newJob.script_content && (
                          <p className="text-xs text-green-600 mt-2">
                            ✅ Script uploaded successfully ({newJob.script_content.split('\n').length} lines)
                          </p>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 mt-2">
                        Supported formats: .py, .js, .bat, .sh, .ps1, .txt
                      </p>
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Timeout (seconds)</label>
                    <input
                      type="number"
                      value={newJob.timeout_seconds}
                      onChange={(e) => setNewJob({...newJob, timeout_seconds: parseInt(e.target.value)})}
                      className={`w-full px-3 py-2 border rounded-lg ${
                        isDarkMode 
                          ? 'bg-gray-700 border-gray-600 text-white' 
                          : 'bg-white border-gray-300 text-gray-900'
                      }`}
                      min="1"
                      max="10000"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Max Retries</label>
                    <input
                      type="number"
                      value={3}
                      onChange={(e) => {}}
                      className={`w-full px-3 py-2 border rounded-lg ${
                        isDarkMode
                          ? 'bg-gray-700 border-gray-600 text-white'
                          : 'bg-white border-gray-300 text-gray-900'
                      }`}
                      min="0"
                      max="10"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Notification Email</label>
                  <input
                    type="email"
                    value=""
                    onChange={(e) => {}}
                    className={`w-full px-3 py-2 border rounded-lg ${
                      isDarkMode 
                        ? 'bg-gray-700 border-gray-600 text-white' 
                        : 'bg-white border-gray-300 text-gray-900'
                    }`}
                    placeholder="admin@example.com"
                  />
                </div>
                
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="enabled"
                    checked={newJob.enabled}
                    onChange={(e) => setNewJob({...newJob, enabled: e.target.checked})}
                    className="mr-2"
                  />
                  <label htmlFor="enabled" className="text-sm">Enable job immediately</label>
                </div>
              </div>
              
              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => setShowCreateModal(false)}
                  className={`px-4 py-2 rounded-lg ${
                    isDarkMode ? 'bg-gray-600 hover:bg-gray-700' : 'bg-gray-200 hover:bg-gray-300'
                  }`}
                >
                  Cancel
                </button>
                <button
                  onClick={createCronJob}
                  disabled={!isJobValid()}
                  className={`px-4 py-2 rounded-lg text-white ${
                    !isJobValid()
                      ? 'bg-gray-400 cursor-not-allowed'
                      : 'bg-green-500 hover:bg-green-600'
                  }`}
                >
                  Create Job
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}

        {/* Edit Job Modal */}
        {showEditModal && editingJob && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
            onClick={(e) => e.target === e.currentTarget && setShowEditModal(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white dark:bg-gray-800 rounded-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto"
            >
              <h3 className="text-xl font-semibold mb-4">Edit Cron Job</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Job Name *</label>
                  <input
                    type="text"
                    value={newJob.name}
                    onChange={(e) => setNewJob({...newJob, name: e.target.value})}
                    className={`w-full px-3 py-2 border rounded-lg ${
                      isDarkMode 
                        ? 'bg-gray-700 border-gray-600 text-white' 
                        : 'bg-white border-gray-300 text-gray-900'
                    }`}
                    placeholder="e.g., Daily Backup"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2">Description</label>
                  <textarea
                    value={newJob.description}
                    onChange={(e) => setNewJob({...newJob, description: e.target.value})}
                    className={`w-full px-3 py-2 border rounded-lg ${
                      isDarkMode 
                        ? 'bg-gray-700 border-gray-600 text-white' 
                        : 'bg-white border-gray-300 text-gray-900'
                    }`}
                    rows="2"
                    placeholder="Brief description of what this job does"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Schedule *
                  </label>
                  
                  {/* Schedule Input Mode Tabs */}
                  <div className="flex mb-3 border-b border-gray-300 dark:border-gray-600">
                    <button
                      type="button"
                      onClick={() => setCronInputMode('preset')}
                      className={`px-3 py-2 text-sm font-medium border-b-2 transition-colors ${
                        cronInputMode === 'preset'
                          ? 'border-blue-500 text-blue-500'
                          : 'border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
                      }`}
                    >
                      <FontAwesomeIcon icon={faClock} className="mr-1" />
                      Quick Schedule
                    </button>
                    <button
                      type="button"
                      onClick={() => setCronInputMode('custom')}
                      className={`px-3 py-2 text-sm font-medium border-b-2 transition-colors ${
                        cronInputMode === 'custom'
                          ? 'border-blue-500 text-blue-500'
                          : 'border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
                      }`}
                    >
                      <FontAwesomeIcon icon={faCode} className="mr-1" />
                      Custom Expression
                    </button>
                  </div>

                  {/* Preset Schedule Dropdown */}
                  {cronInputMode === 'preset' && (
                    <div>
                      <select
                        value={newJob.cron_expression}
                        onChange={(e) => setNewJob({...newJob, cron_expression: e.target.value})}
                        className={`w-full px-3 py-2 border rounded-lg ${
                          isDarkMode 
                            ? 'bg-gray-700 border-gray-600 text-white' 
                            : 'bg-white border-gray-300 text-gray-900'
                        }`}
                      >
                        {cronPresets.map((preset) => (
                          <option key={preset.value} value={preset.value}>
                            {preset.label}
                          </option>
                        ))}
                      </select>
                      <p className="text-xs text-gray-500 mt-1">
                        Choose from common scheduling patterns
                      </p>
                    </div>
                  )}

                  {/* Custom Cron Expression */}
                  {cronInputMode === 'custom' && (
                    <div>
                      <input
                        type="text"
                        value={newJob.cron_expression}
                        onChange={(e) => setNewJob({...newJob, cron_expression: e.target.value})}
                        className={`w-full px-3 py-2 border rounded-lg ${
                          isDarkMode 
                            ? 'bg-gray-700 border-gray-600 text-white' 
                            : 'bg-white border-gray-300 text-gray-900'
                        }`}
                        placeholder="0 */2 * * *"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Format: minute hour day month weekday<br/>
                        Examples: "0 */2 * * *" (every 2 hours), "*/15 * * * *" (every 15 minutes)
                      </p>
                    </div>
                  )}

                  {/* Show human readable preview */}
                  <div className={`mt-2 p-2 rounded-lg text-sm ${
                    isDarkMode ? 'bg-gray-700' : 'bg-blue-50'
                  }`}>
                    <FontAwesomeIcon icon={faClock} className="mr-2 text-blue-500" />
                    <span className="text-blue-600 dark:text-blue-400">
                      Will run: {cronToHuman(newJob.cron_expression)}
                    </span>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2">Script/Command *</label>
                  
                  {/* Script Input Mode Tabs */}
                  <div className="flex mb-3 border-b border-gray-300 dark:border-gray-600">
                    {[
                      { key: 'command', label: 'Command', icon: faCode },
                      { key: 'path', label: 'File Path', icon: faFolder },
                      { key: 'paste', label: 'Paste Script', icon: faCopy },
                      { key: 'upload', label: 'Upload File', icon: faUpload }
                    ].map((tab) => (
                      <button
                        key={tab.key}
                        type="button"
                        onClick={() => setScriptInputMode(tab.key)}
                        className={`px-3 py-2 text-sm font-medium border-b-2 transition-colors ${
                          scriptInputMode === tab.key
                            ? 'border-blue-500 text-blue-500'
                            : 'border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
                        }`}
                      >
                        <FontAwesomeIcon icon={tab.icon} className="mr-1" />
                        {tab.label}
                      </button>
                    ))}
                  </div>

                  {/* Command Input */}
                  {scriptInputMode === 'command' && (
                    <textarea
                      value={newJob.command}
                      onChange={async (e) => {
                        const command = e.target.value;
                        console.log('🔍 [FRONTEND] Command changed, triggering validation...');
                        setNewJob({...newJob, command});
                        await handleScriptContentChange(newJob.script_content, command);
                      }}
                      className={`w-full px-3 py-2 border rounded-lg ${
                        isDarkMode 
                          ? 'bg-gray-700 border-gray-600 text-white' 
                          : 'bg-white border-gray-300 text-gray-900'
                      }`}
                      rows="3"
                      placeholder="python /path/to/script.py"
                    />
                  )}

                  {/* File Path Input */}
                  {scriptInputMode === 'path' && (
                    <div>
                      <input
                        type="text"
                        value={newJob.script_path}
                        onChange={async (e) => {
                          const path = e.target.value;
                          console.log('🔍 [FRONTEND] Script path changed:', path);
                          setNewJob({...newJob, script_path: path});
                          
                          // For path mode, don't validate file content (backend will handle file reading)
                          // Only validate if user wants to restrict path-based validation
                          if (path.trim()) {
                            // Skip validation for path mode - let backend handle it
                            setScriptValidation(null);
                            setShowValidationWarning(false);
                          } else {
                            setScriptValidation(null);
                            setShowValidationWarning(false);
                          }
                        }}
                        className={`w-full px-3 py-2 border rounded-lg ${
                          isDarkMode 
                            ? 'bg-gray-700 border-gray-600 text-white' 
                            : 'bg-white border-gray-300 text-gray-900'
                        }`}
                        placeholder="/path/to/your/script.py"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Full path to your script file (e.g., Python, batch, shell script)
                      </p>
                    </div>
                  )}

                  {/* Paste Script Input */}
                  {scriptInputMode === 'paste' && (
                    <div>
                      <textarea
                        value={newJob.script_content}
                        onChange={async (e) => {
                          const content = e.target.value;
                          console.log('🔍 [FRONTEND] Paste script content changed, triggering validation...');
                          setNewJob({...newJob, script_content: content});
                          await handleScriptContentChange(content, newJob.command);
                        }}
                        className={`w-full px-3 py-2 border rounded-lg font-mono text-sm ${
                          isDarkMode 
                            ? 'bg-gray-700 border-gray-600 text-white' 
                            : 'bg-white border-gray-300 text-gray-900'
                        }`}
                        rows="8"
                        placeholder={`# Python script example
import datetime

print(f"Script executed at: {datetime.datetime.now()}")
print("Hello from cron job!")`}
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Paste your complete script here. It will be executed as Python code.
                      </p>
                    </div>
                  )}

                  {/* File Upload Input */}
                  {scriptInputMode === 'upload' && (
                    <div>
                      <div className={`border-2 border-dashed rounded-lg p-6 text-center ${
                        isDarkMode 
                          ? 'border-gray-600 hover:border-gray-500' 
                          : 'border-gray-300 hover:border-gray-400'
                      } transition-colors`}>
                        <FontAwesomeIcon icon={faFileCode} className="text-3xl text-gray-400 mb-3" />
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                          Click to upload a script file or drag and drop
                        </p>
                        <input
                          type="file"
                          onChange={handleFileUpload}
                          accept=".py,.js,.bat,.sh,.ps1,.txt"
                          className="hidden"
                          id="script-upload-edit"
                        />
                        <label
                          htmlFor="script-upload-edit"
                          className={`inline-flex items-center px-4 py-2 rounded-lg cursor-pointer transition-colors ${
                            isDarkMode
                              ? 'bg-blue-600 hover:bg-blue-700 text-white'
                              : 'bg-blue-500 hover:bg-blue-600 text-white'
                          }`}
                        >
                          <FontAwesomeIcon icon={faUpload} className="mr-2" />
                          Choose File
                        </label>
                        {newJob.script_content && (
                          <p className="text-xs text-green-600 mt-2">
                            ✅ Script uploaded successfully ({newJob.script_content.split('\n').length} lines)
                          </p>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 mt-2">
                        Supported formats: .py, .js, .bat, .sh, .ps1, .txt
                      </p>
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Timeout (seconds)</label>
                    <input
                      type="number"
                      value={newJob.timeout_seconds}
                      onChange={(e) => setNewJob({...newJob, timeout_seconds: parseInt(e.target.value)})}
                      className={`w-full px-3 py-2 border rounded-lg ${
                        isDarkMode 
                          ? 'bg-gray-700 border-gray-600 text-white' 
                          : 'bg-white border-gray-300 text-gray-900'
                      }`}
                      min="1"
                      max="10000"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Max Retries</label>
                    <input
                      type="number"
                      value={3}
                      onChange={(e) => {}}
                      className={`w-full px-3 py-2 border rounded-lg ${
                        isDarkMode
                          ? 'bg-gray-700 border-gray-600 text-white'
                          : 'bg-white border-gray-300 text-gray-900'
                      }`}
                      min="0"
                      max="10"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Notification Email</label>
                  <input
                    type="email"
                    value=""
                    onChange={(e) => {}}
                    className={`w-full px-3 py-2 border rounded-lg ${
                      isDarkMode 
                        ? 'bg-gray-700 border-gray-600 text-white' 
                        : 'bg-white border-gray-300 text-gray-900'
                    }`}
                    placeholder="admin@example.com"
                  />
                </div>
                
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="enabled-edit"
                    checked={newJob.enabled}
                    onChange={(e) => setNewJob({...newJob, enabled: e.target.checked})}
                    className="mr-2"
                  />
                  <label htmlFor="enabled-edit" className="text-sm">Enable job immediately</label>
                </div>
              </div>
              
              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => setShowEditModal(false)}
                  className={`px-4 py-2 rounded-lg ${
                    isDarkMode ? 'bg-gray-600 hover:bg-gray-700' : 'bg-gray-200 hover:bg-gray-300'
                  }`}
                >
                  Cancel
                </button>
                <button
                  onClick={updateCronJob}
                  disabled={!isJobValid()}
                  className={`px-4 py-2 rounded-lg text-white ${
                    !isJobValid()
                      ? 'bg-gray-400 cursor-not-allowed'
                      : 'bg-blue-500 hover:bg-blue-600'
                  }`}
                >
                  Update Job
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}

        {/* Scheduler Conflict Modal */}
        {showSchedulerConflictModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
            onClick={(e) => e.target === e.currentTarget && setShowSchedulerConflictModal(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: -20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: -20 }}
              className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 rounded-2xl p-8 w-full max-w-md border shadow-2xl"
            >
              {/* Icon and Header */}
              <div className="text-center mb-6">
                <div className="mx-auto w-16 h-16 bg-red-100 dark:bg-red-900 rounded-full flex items-center justify-center mb-4">
                  <svg className="w-8 h-8 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 14.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
                <h3 className="text-2xl font-bold text-red-600 dark:text-red-400 mb-2">
                  Job Creation Blocked
                </h3>
                <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  Scheduler Conflict Detected
                </p>
              </div>

              {/* Content */}
              <div className="mb-6">
                <div className={`p-4 rounded-lg mb-4 ${
                  isDarkMode ? 'bg-red-900 bg-opacity-30 border-red-800' : 'bg-red-50 border-red-200'
                } border`}>
                  <p className={`text-sm leading-relaxed ${
                    isDarkMode ? 'text-red-200' : 'text-red-800'
                  }`}>
                    Your script contains <strong>built-in scheduler components</strong> that will conflict with this cron system.
                  </p>
                </div>

                {scriptValidation && scriptValidation.scheduler_conflicts && (
                  <div className="mb-4">
                    <p className={`text-sm font-medium mb-2 ${
                      isDarkMode ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                      Detected conflicts:
                    </p>
                    <ul className={`text-sm space-y-1 ${
                      isDarkMode ? 'text-gray-400' : 'text-gray-600'
                    }`}>
                      {scriptValidation.scheduler_conflicts.slice(0, 3).map((conflict, idx) => (
                        <li key={idx} className="flex items-center">
                          <span className="w-2 h-2 bg-red-500 rounded-full mr-2 flex-shrink-0"></span>
                          {conflict.description}
                        </li>
                      ))}
                      {scriptValidation.scheduler_conflicts.length > 3 && (
                        <li className="text-xs text-gray-500 ml-4">
                          ... and {scriptValidation.scheduler_conflicts.length - 3} more
                        </li>
                      )}
                    </ul>
                  </div>
                )}

                <div className={`p-4 rounded-lg ${
                  isDarkMode ? 'bg-blue-900 bg-opacity-20 border-blue-800' : 'bg-blue-50 border-blue-200'
                } border`}>
                  <div className="flex items-start">
                    <svg className="w-5 h-5 text-blue-600 dark:text-blue-400 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div>
                      <p className={`text-sm font-medium mb-1 ${
                        isDarkMode ? 'text-blue-300' : 'text-blue-800'
                      }`}>
                        💡 Solution
                      </p>
                      <p className={`text-sm ${
                        isDarkMode ? 'text-blue-200' : 'text-blue-700'
                      }`}>
                        Remove scheduler libraries and infinite loops from your script. 
                        This cron system will handle the scheduling automatically.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-center">
                <button
                  onClick={() => setShowSchedulerConflictModal(false)}
                  className={`px-6 py-3 rounded-lg font-medium transition-all duration-200 ${
                    isDarkMode 
                      ? 'bg-gray-700 hover:bg-gray-600 text-white' 
                      : 'bg-gray-100 hover:bg-gray-200 text-gray-800'
                  } focus:ring-4 focus:ring-gray-300 dark:focus:ring-gray-600`}
                >
                  I Understand
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      </div>
    </MainLayout>
  );
};

export default CronLogs;